import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Table, Tooltip, Whisper } from 'rsuite';
import { TableProps } from 'rsuite/lib/Table';
import { BaseCell, CompetitorPriceCell, HoverNomeColuna, MargemCell, PMZCell, PrecoSugeridoCell, PrecoVigenteCell } from '../../..';
import { GerenciadorPrecos } from '../../../../../../../@types/GerenciadorPrecos';
import { ButtonPrimary, Layout } from '../../../../../../../components';
import { AlertWithTimestamp } from '../../../../../../../components/AlertWithTimestamp';
import { Exception } from '../../../../../../../data';
import { RowData } from '../../../../../../../reducers/gerenciadorPrecos/datapoints';
import { checkIfIsLoading } from '../../../../../../../utils';
import { handleMoveFocusToNextRowInput } from '../../../../utils';
import * as calcHandlers from '../../../../utils/utils';
import { restoreModalRow } from '../../../../utils/restoreModalRow';
import { CpiCell } from '../../../TableCells/CpiCell';

import { MdPallet } from 'react-icons/md';
import FatorCell from '../../../../../../../components/TableCells/FatorCell/FatorCell';
import ProductFamilyClusterCell from '../../../../../../../components/TableCells/ProductFamilyClusterCell/ProductFamilyClusterCell';
import { DatapointExhibitionTypeState, SET_GET_STORE_FAMILY_CLUSTER_MODAL_IS_OPEN } from '../../../../../../../reducers/gerenciadorPrecos';
import {
  RESET_GET_STORE_FAMILY_CLUSTER_MODAL_DATAPOINTS,
  selectorGetStoreFamilyClusterModalDatapoints,
  SET_GET_STORE_FAMILY_CLUSTER_MODAL_DATAPOINTS,
  UPDATE_GET_STORE_FAMILY_CLUSTER_MODAL_DATAPOINT,
} from '../../../../../../../reducers/getStoreFamilyClusterModal/datapoints';
import {
  CLEAR_EDITED_STORE_FAMILY_CLUSTER_DATAPOINTS,
  selectorGetEditedStoreFamilyClusterDatapoints,
  UPDATE_EDITED_STORE_FAMILY_CLUSTER_DATAPOINT,
} from '../../../../../../../reducers/getStoreFamilyClusterModal/editedStoreFamilyClusterDatapoints';
import {
  RESET_GET_STORE_FAMILY_CLUSTER_MODAL_PAGINATION,
  selectorGetStoreFamilyClusterModalPagination,
  SET_GET_STORE_FAMILY_CLUSTER_MODAL_ACTIVE_PAGE,
  SET_GET_STORE_FAMILY_CLUSTER_MODAL_DISPLAY_LENGTH,
} from '../../../../../../../reducers/getStoreFamilyClusterModal/pagination';
import {
  RESET_GET_STORE_FAMILY_CLUSTER_MODAL_SORT,
  selectorGetStoreFamilyClusterModalSort,
  SET_GET_STORE_FAMILY_CLUSTER_MODAL_SORT,
} from '../../../../../../../reducers/getStoreFamilyClusterModal/sort';
import {
  RESET_GET_STORE_FAMILY_CLUSTER_MODAL_TOTAL_ELEMENTS,
  selectorGetStoreFamilyClusterModalTotalElements,
  SET_GET_STORE_FAMILY_CLUSTER_MODAL_TOTAL_ELEMENTS,
} from '../../../../../../../reducers/getStoreFamilyClusterModal/totalElements';
import { useEditedPriceManagerStore } from '../../../../../../../store/priceManagerStore';
import { useBigNumbersRefresh } from '../../../../hooks/useBigNumbersRefresh';
import { GET_ITEMS_STORE_FAMILY_CLUSTER_MODAL, UPDATE_PRODUCT_STORE_FAMILY_CLUSTER_PRICES } from '../../service';
import { ContentStoreFamilyClusterModal, GET_ITEMS_STORE_FAMILY_CLUSTER_MODAL_RESPONSE, ProductDataDTO, UpdateProductDataDTO } from '../../types';
import { GET_STORE_FAMILY_CLUSTER_DATAPOINTS_MODEL } from '../../utils';
import styles from './GetStoreFamilyClusterTable.module.scss';

const { Column, HeaderCell, Cell, Pagination } = Table;

const DATA_KEY = 'itemsStoreFamilyCluster';

const MemoCpiCell = memo(CpiCell);
const MemoHoverNomeColuna = memo(HoverNomeColuna);
const MemoProductFamilyClusterCell = memo(ProductFamilyClusterCell);
const MemoFatorCell = memo(FatorCell);

// O modal salva em lote pelo botão SALVAR ALTERAÇÕES: sair do campo não persiste nada
const handleBlurNoop = () => undefined;

const GetStoreFamilyClusterTable = ({
  onHide,
  productId,
  storeId,
  productFamilyId,
  clusterId,
  datapointExhibitionType,
}: {
  onHide: () => void;

  productId: string;
  storeId: string;
  productFamilyId: string;
  clusterId: string;
  datapointExhibitionType: DatapointExhibitionTypeState['datapointExhibitionType'];
}) => {
  const queryClient = useQueryClient();
  const { markBigNumbersOutdated } = useBigNumbersRefresh();
  const editedDatapoints = useSelector(selectorGetEditedStoreFamilyClusterDatapoints);
  const dispatch = useDispatch();
  const pagination = useSelector(selectorGetStoreFamilyClusterModalPagination);
  const totalElements = useSelector(selectorGetStoreFamilyClusterModalTotalElements);
  const sort = useSelector(selectorGetStoreFamilyClusterModalSort);
  const datapoints = useSelector(selectorGetStoreFamilyClusterModalDatapoints);

  const { isPendingEditedPrice } = useEditedPriceManagerStore();

  const handleSortColumn = useCallback(
    (sortColumn: string, sortType: TableProps['sortType']) => {
      dispatch(
        SET_GET_STORE_FAMILY_CLUSTER_MODAL_SORT({
          type: sortColumn,
          orderBy: sortType,
        }),
      );
    },
    [dispatch],
  );

  const closeGetStoreFamilyClusterModal = () => {
    onHide();
    dispatch(SET_GET_STORE_FAMILY_CLUSTER_MODAL_IS_OPEN(false));
    dispatch(CLEAR_EDITED_STORE_FAMILY_CLUSTER_DATAPOINTS());
    dispatch(RESET_GET_STORE_FAMILY_CLUSTER_MODAL_DATAPOINTS());
    dispatch(RESET_GET_STORE_FAMILY_CLUSTER_MODAL_PAGINATION());
    dispatch(RESET_GET_STORE_FAMILY_CLUSTER_MODAL_SORT());
    dispatch(RESET_GET_STORE_FAMILY_CLUSTER_MODAL_TOTAL_ELEMENTS());
  };

  const GET_DATAPOINTS_MODEL = useMemo(() => {
    return GET_STORE_FAMILY_CLUSTER_DATAPOINTS_MODEL({
      sort,
      pagination,
      clusterId,
      productId,
      storeId,
      productFamilyId,
      datapointExhibitionType,
    });
  }, [sort, pagination, clusterId, productId, storeId, productFamilyId, datapointExhibitionType]);

  const handleGetDatapoints = useCallback(async () => {
    try {
      const res = await GET_ITEMS_STORE_FAMILY_CLUSTER_MODAL(GET_DATAPOINTS_MODEL);

      return res;
    } catch {
      throw new Error('Erro ao buscar produtos');
    }
  }, [GET_DATAPOINTS_MODEL]);

  const handleSuccess = useCallback(
    (data: GET_ITEMS_STORE_FAMILY_CLUSTER_MODAL_RESPONSE) => {
      const updatedContent = data.content.map(item => {
        const editedItem = editedDatapoints[item.productsToBePricedId];
        if (editedItem) {
          return {
            ...item,
            ...editedItem,
          };
        }
        return item;
      });

      dispatch(SET_GET_STORE_FAMILY_CLUSTER_MODAL_DATAPOINTS(updatedContent));
      dispatch(SET_GET_STORE_FAMILY_CLUSTER_MODAL_TOTAL_ELEMENTS(data.totalElements));
    },
    [dispatch, editedDatapoints],
  );

  const handleChangePage = useCallback(
    (page: number) => {
      dispatch(SET_GET_STORE_FAMILY_CLUSTER_MODAL_ACTIVE_PAGE(page - 1));
    },
    [dispatch],
  );

  const handleChangeLength = useCallback(
    (size: number) => {
      const lastValidIndex = Math.ceil(totalElements / size);

      const isOnInvalidLastIndex = pagination.activePage + 1 > lastValidIndex;

      if (isOnInvalidLastIndex) handleChangePage(lastValidIndex);

      dispatch(SET_GET_STORE_FAMILY_CLUSTER_MODAL_DISPLAY_LENGTH(size));
    },
    [dispatch, totalElements, pagination.activePage, handleChangePage],
  );

  const memoPagination = useMemo(() => {
    return {
      total: totalElements,
      activePage: pagination.activePage + 1,
      displayLength: pagination.displayLength,
      lengthMenu: [
        { value: 25, label: '25' },
        { value: 50, label: '50' },
        { value: 100, label: '100' },
      ],
      onChangePage: handleChangePage,
      onChangeLength: handleChangeLength,
    };
  }, [totalElements, pagination, handleChangePage, handleChangeLength]);

  const { fetchStatus } = useQuery({
    queryKey: ['ipa/gerenciador/storeFamilyCluster', GET_DATAPOINTS_MODEL],
    queryFn: handleGetDatapoints,
    onSuccess: handleSuccess,
    retry: false,
    enabled: !isPendingEditedPrice,
  });

  const isLoading = useMemo(() => checkIfIsLoading(fetchStatus), [fetchStatus]);

  const handleRestoreRow = useCallback(
    (rowData: RowData, index: number) => {
      restoreModalRow({
        dispatch,
        rowData,
        index,
        editedDatapoints,
        updateDatapoint: UPDATE_GET_STORE_FAMILY_CLUSTER_MODAL_DATAPOINT,
        updateEditedDatapoint: UPDATE_EDITED_STORE_FAMILY_CLUSTER_DATAPOINT,
      });
    },
    [dispatch, editedDatapoints],
  );

  const handleUpdateCampo = useCallback(
    (name: GerenciadorPrecos.Inputs, value: number, rowData: RowData, index: number) => {
      let updatedData: Partial<RowData> = {
        priceStatus: 'EDITADO' as 'EDITED',
      };

      if (value > 0) {
        updatedData = { ...updatedData, novoCalculo: true };
        switch (name) {
          case 'novoPreco':
            updatedData = {
              ...updatedData,
              ...calcHandlers.handleCalculoPreco(value, rowData),
            };
            break;
          case 'novaCompetitividade':
            updatedData = {
              ...updatedData,
              ...calcHandlers.handleCalculoCompetitividade(value, rowData),
            };
            break;
          case 'novaMargem':
            updatedData = {
              ...updatedData,
              ...calcHandlers.handleCalculoMargem(value, rowData),
            };
            break;
          default:
            break;
        }
      } else {
        updatedData = {
          ...updatedData,
          price: value,
          novaMargem: 0,
          novaCompetitividade: 0,
        };
      }

      dispatch(
        UPDATE_EDITED_STORE_FAMILY_CLUSTER_DATAPOINT({
          key: rowData.productsToBePricedId,
          updatedData: {
            ...rowData,
            ...updatedData,
          },
        }),
      );

      dispatch(
        UPDATE_GET_STORE_FAMILY_CLUSTER_MODAL_DATAPOINT({
          index,
          updatedData,
        }),
      );
    },
    [dispatch],
  );

  const formattedData = useMemo((): UpdateProductDataDTO => {
    const productDataDTOList: ProductDataDTO[] = Object.keys(editedDatapoints).map(productsToBePricedId => {
      const editedItem = editedDatapoints[productsToBePricedId];
      return {
        productsToBePricedId,
        editedPrice: editedItem.price ?? 0,
      };
    });
    return {
      productDataDTOList,
    };
  }, [editedDatapoints]);

  const { mutate } = useMutation(UPDATE_PRODUCT_STORE_FAMILY_CLUSTER_PRICES, {
    onSuccess: () => {
      Alert.success('Alterações realizadas com sucesso!');
      queryClient.invalidateQueries(['ipa.gerenciador.datapoints']);
      markBigNumbersOutdated();
    },
    onError: () => {
      const { message } = new Exception('Erro ao atualizar preços!');
      AlertWithTimestamp({ message });
    },
  });

  const handleSaveChanges = () => {
    if (Object.keys(editedDatapoints).length === 0) {
      closeGetStoreFamilyClusterModal();
    } else {
      mutate(formattedData);
      dispatch(CLEAR_EDITED_STORE_FAMILY_CLUSTER_DATAPOINTS());
      dispatch(SET_GET_STORE_FAMILY_CLUSTER_MODAL_IS_OPEN(false));
      closeGetStoreFamilyClusterModal();
    }
  };

  useEffect(() => {
    const tableContainer = document.getElementById('store-family-cluster-table');
    if (tableContainer) {
      tableContainer.addEventListener('keydown', e => {
        handleMoveFocusToNextRowInput(e) as unknown as HTMLInputElement;
      });
    }
    return () => {
      if (tableContainer) {
        tableContainer.removeEventListener('keydown', e => {
          handleMoveFocusToNextRowInput(e) as unknown as HTMLInputElement;
        });
      }
    };
  }, []);

  return (
    <Layout.Section padded className={styles['layout-section']}>
      <Table
        id="store-family-cluster-table"
        dataKey={DATA_KEY}
        data={datapoints}
        className={classNames('ipa-table')}
        loading={isLoading || isPendingEditedPrice}
        sortColumn={sort.type}
        sortType={sort.orderBy as TableProps['sortType']}
        headerHeight={46}
        rowHeight={62}
        onSortColumn={handleSortColumn}
        height={500}
      >
        {/* @ts-ignore */}
        <Column sortable flexGrow={1}>
          <HeaderCell>Produto</HeaderCell>
          <Cell dataKey="productIds">{(rowData: ContentStoreFamilyClusterModal) => <MemoProductFamilyClusterCell rowData={rowData} />}</Cell>
        </Column>

        {/* @ts-ignore */}
        <Column sortable width={64}>
          <HeaderCell>Loja</HeaderCell>
          <Cell dataKey="storeId">
            {(rowData: RowData) => (
              <BaseCell rowData={rowData}>
                <BaseCell.MainContainer>{rowData.storeId}</BaseCell.MainContainer>
              </BaseCell>
            )}
          </Cell>
        </Column>
        {/* @ts-ignore */}
        <Column sortable width={100}>
          <HeaderCell>Fator</HeaderCell>
          <Cell dataKey="factor">{(rowData: ContentStoreFamilyClusterModal) => <MemoFatorCell rowData={rowData} />}</Cell>
        </Column>

        {/* @ts-ignore */}

        <Column sortable width={120}>
          <HeaderCell>Estoque</HeaderCell>
          <Cell dataKey="stock_quantity">
            {(rowData: RowData) => (
              <BaseCell rowData={rowData}>
                <div className={styles['estoque-venda-container']}>
                  <div className={styles['estoque-venda-top']}>
                    <span className={styles['estoque-venda-icon']}>
                      <MdPallet width={11} height={11} />
                    </span>
                    <Whisper placement="top" speaker={<Tooltip>Estoque total na loja</Tooltip>}>
                      <span className={styles['estoque-venda-numero']}>{rowData.stock?.quantity ?? 0}</span>
                    </Whisper>
                  </div>
                  <div className={styles['estoque-venda-bottom']}>
                    <Whisper placement="left" speaker={<Tooltip>Dias de estoque</Tooltip>}>
                      <span className={styles['estoque-venda-badge']}>{rowData.stock?.coverageDays ? `${Math.round(rowData.stock.coverageDays)}d` : '-'}</span>
                    </Whisper>
                    <Whisper placement="right" speaker={<Tooltip>Data última entrada do produto</Tooltip>}>
                      <span className={styles['estoque-venda-data']}>
                        {rowData.stock?.lastUpdated && dayjs(rowData.stock.lastUpdated).isValid()
                          ? dayjs(rowData.stock.lastUpdated).format('DD/MM/YYYY')
                          : '--'}
                      </span>
                    </Whisper>
                  </div>
                </div>
              </BaseCell>
            )}
          </Cell>
        </Column>
        {/* @ts-ignore */}
        <Column sortable minWidth={78}>
          <HeaderCell className={styles['gerenciador-page__table__header-cell']}>
            <div>
              <p>PMZ e</p>
              <p>custo</p>
            </div>
          </HeaderCell>
          <Cell dataKey="pmz">{(rowData: RowData) => <PMZCell rowData={rowData} />}</Cell>
        </Column>
        {/* @ts-ignore */}
        <Column sortable width={124}>
          <HeaderCell className={styles['gerenciador-page__table__header-cell']}>
            <div>
              <p>Preço</p>
              <p>vigente</p>
            </div>
          </HeaderCell>
          <Cell dataKey="priceRetail">{(rowData: RowData) => <PrecoVigenteCell rowData={rowData} />}</Cell>
        </Column>
        {/* @ts-ignore */}
        <Column sortable width={127}>
          <HeaderCell className={styles['gerenciador-page__table__header-cell']}>
            <div>
              <p>Preço</p>
              <p>concorrente</p>
            </div>
          </HeaderCell>
          <Cell dataKey="competitorsPrice">{(rowData: RowData) => <CompetitorPriceCell rowData={rowData} />}</Cell>
        </Column>
        {/* @ts-ignore */}
        <Column width={132}>
          <HeaderCell>Preço sugerido</HeaderCell>
          <Cell className={styles['table__input-group']} dataKey="price">
            {(rowData: RowData, index: number) => (
              <PrecoSugeridoCell
                className={classNames(styles['table__input-group'], styles['gerenciador-page__table__input-group'])}
                dataKey="novoPreco"
                rowData={rowData}
                index={index}
                handleUpdateCampo={handleUpdateCampo}
                handleBlur={handleBlurNoop}
              />
            )}
          </Cell>
        </Column>

        {/* @ts-ignore */}
        <Column width={116}>
          <HeaderCell className={styles['gerenciador-page__table__header-cell']}>Margem</HeaderCell>
          <Cell className={styles['table__input-group']} dataKey="newMargin">
            {(rowData: RowData, index: number) => (
              <MargemCell
                dataKey="novaMargem"
                rowData={rowData}
                index={index}
                handleUpdateCampo={handleUpdateCampo}
                handleRestoreRow={handleRestoreRow}
                handleBlur={handleBlurNoop}
              />
            )}
          </Cell>
        </Column>
        {/* @ts-ignore */}
        <Column width={116}>
          <HeaderCell>
            <MemoHoverNomeColuna placement="bottom" text="CPI" tooltip="Competitividade" />
          </HeaderCell>
          <Cell dataKey="cpi">
            {(rowData: RowData, index: number) => (
              <MemoCpiCell
                dataKey="novaCompetitividade"
                rowData={rowData}
                index={index}
                handleUpdateCampo={handleUpdateCampo}
                handleBlur={handleBlurNoop}
              />
            )}
          </Cell>
        </Column>
      </Table>
      <Pagination {...memoPagination} />
      <div className={styles.footer}>
        <ButtonPrimary onClick={closeGetStoreFamilyClusterModal} type="reset" theme="ghost">
          FECHAR
        </ButtonPrimary>
        <ButtonPrimary onClick={handleSaveChanges} type="submit">
          SALVAR ALTERAÇÕES
        </ButtonPrimary>
      </div>
    </Layout.Section>
  );
};

export default GetStoreFamilyClusterTable;
